import http from "./http-common";

class IpdService {
  queryString(payload) {
    return Object.keys(payload)
      .map((key) =>
        key &&
        payload[key] !== undefined &&
        payload[key] !== null &&
        payload[key] !== ""
          ? key + "=" + payload[key]
          : ""
      )
      .filter(Boolean)
      .join("&");
  }

  // ── IPD Admission ──────────────────────────────────────────────────────

  /**
   * GET /ipdAdmission/list?patientId=&pageNo=&pageSize=
   * Returns paginated list of IPD admissions.
   */
  getIpdList(payload) {
    const queryString = this.queryString(payload);
    return http.get(`/ipdAdmission/list?${queryString}`);
  }

  /**
   * GET /ipdAdmission/:admissionId
   * Returns a single IPD admission by ID.
   */
  getIpdById(admissionId) {
    return http.get(`/ipdAdmission/${admissionId}`);
  }

  /**
   * POST /ipdAdmission/
   * Creates a new IPD admission.
   */
  addIpdAdmission(payload) {
    return http.post("/ipdAdmission/", payload);
  }

  /**
   * PUT /ipdAdmission/:admissionId
   * Updates an existing IPD admission.
   */
  updateIpdAdmission(admissionId, payload) {
    return http.put(`/ipdAdmission/${admissionId}`, payload);
  }

  /**
   * PUT /ipdAdmission/:admissionId/assignedDoctor?assignedDoctorId=&modifiedBy=
   * Quick reassignment of the admission's primary doctor (does not touch the
   * ward/nurse/duty-doctor roster — see ipdPatientAssignment/* below for that).
   */
  assignDoctor(admissionId, params) {
    const queryString = this.queryString(params);
    return http.put(`/ipdAdmission/${admissionId}/assignedDoctor?${queryString}`);
  }

  // ── IPD Patient Assignment (ward / bed / nurse / duty-doctor roster) ────
  // Sub-resource of an IPD admission; kept in this same service per module
  // structure rather than a separate IpdAssignmentService.

  /**
   * POST /ipdPatientAssignment/assign
   * Creates a new IPD assignment (primary doctor + nurse shifts + duty doctor shifts).
   */
  assignIpdPatient(payload) {
    return http.post("/ipdPatientAssignment/assign", payload);
  }

  /**
   * PUT /ipdPatientAssignment/status/:assignmentId?status=&modifiedBy=
   */
  updateIpdAssignmentStatus(assignmentId, status, modifiedBy) {
    const queryString = this.queryString({ status, modifiedBy });
    return http.put(`/ipdPatientAssignment/status/${assignmentId}?${queryString}`);
  }

  /**
   * GET /ipdPatientAssignment/:assignmentId
   */
  getIpdAssignmentById(assignmentId) {
    return http.get(`/ipdPatientAssignment/${assignmentId}`);
  }

  /**
   * GET /ipdPatientAssignment/active/:admissionId
   * Returns the currently ACTIVE assignment (with nested nurseAssignments /
   * dutyDoctors) for a given admission. Drives the Nurses column, Last
   * Assigned column, Manage modal prefill, and History modal.
   */
  getActiveIpdAssignment(admissionId) {
    return http.get(`/ipdPatientAssignment/active/${admissionId}`);
  }

  /**
   * GET /ipdPatientAssignment/list?accountId=&pageNo=&pageSize=
   */
  getIpdAssignmentList({ accountId, pageNo = 1, pageSize = 10 }) {
    const queryString = this.queryString({ accountId, pageNo, pageSize });
    return http.get(`/ipdPatientAssignment/list?${queryString}`);
  }

  /**
   * PUT /ipdPatientAssignment/:assignmentId/nurse
   * Adds/updates a single nurse shift row on an existing assignment.
   */
  upsertIpdNurseAssignment(assignmentId, nursePayload) {
    return http.put(`/ipdPatientAssignment/${assignmentId}/nurse`, nursePayload);
  }

  /**
   * PUT /ipdPatientAssignment/:assignmentId/dutyDoctor
   * Adds/updates a single duty-doctor shift row on an existing assignment.
   */
  upsertIpdDutyDoctorAssignment(assignmentId, doctorPayload) {
    return http.put(`/ipdPatientAssignment/${assignmentId}/dutyDoctor`, doctorPayload);
  }

  getPatientNurseDischargeList(payload) {
    const queryString = this.queryString(payload);
    return http.get(`/patientNurseDischarge/list/filter?${queryString}`);
  }

  getInPatientCount(payload) {
    const queryString = this.queryString(payload);
    return http.get(`/ipdAdmission/ipd/count?${queryString}`);
  }
}

export default new IpdService();